import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Col, DatePicker, Input, Radio, Switch, Tooltip } from 'antd';
import { ErrorMessage, useFormikContext } from 'formik';
import moment from 'moment';
import InputMask from 'react-input-mask';
import { T } from 'react-translator-component';

import * as S from './styled';

import { tooltipDynamicPricing } from '../../../constants/tooltipDynamicPricing';

interface SetPricingPageFormProps {
  loading: boolean;
  selectedValue?: SetPricingPageFormValues[];
  dayOfWeek: string;
  isException: boolean;
  dynamicPricingDetailUuid?: string;
}

export interface SetPricingPageFormValues {
  dynamicPricingDetailUuid: string;
  dynamicPricingUuid: string;
  servicePrice: string;
  kwhPrice: string;
  durationPrice: string;
  operationFee: string;
  taxes: string;
  idleTimePrice: string;
  reservePrice: string;
  moneyPerKilowattCost: string;
  dayOfWeek: string;
  everyDayOfWeek: boolean;
  startTimeGMT?: string;
  endTimeGMT?: string;
  general?: boolean;
  active?: boolean;
  exception: boolean;
  startDate?: string;
  endDate?: string;
  id?: number;
  customIdleTime?: number | string;
  isCustomIdleTimeEnabled?: boolean;
}

export const maskPercent = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return '';

  const percent = Number(
    String(value).replace(/[%]/g, '').replace(/,/g, '.'),
  );

  if (!Number.isFinite(percent)) return '';

  const [integer, decimal] = percent.toFixed(1).split('.');

  return `${integer.padStart(2, '0')},${decimal}%`;
};

export const mascaraMoeda = (value: any) => {
  const digitsFloat = rawMaskMoney(value);

  return maskCurrency(digitsFloat);
};

const rawMaskMoney = (value: string) => {
  const valueString = String(value);

  const onlyDigits = valueString
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);

  return digitsFloat;
};

const maskCurrency = (valor, locale = 'pt-BR', currency = 'BRL') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(valor);
};

const formatOnlyNumber = (value: string | number) => {
  const numericValue = Number(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const rawDigitsMask = (value: string) => {
  const onlyDigits = String(value).replace(/\D/g, '');
  const numericValue = onlyDigits.length > 0 ? onlyDigits : 0;

  return formatOnlyNumber(numericValue);
};

const SetPricingPageForm = ({
  loading,
  selectedValue,
  dayOfWeek,
  isException,
  dynamicPricingDetailUuid,
}: SetPricingPageFormProps) => {
  const { setFieldValue, values, handleChange, errors, setErrors } =
    useFormikContext<SetPricingPageFormValues>();

  const [isInitialized, setIsInitialized] = useState(false);

  const [disabledFields, setDisabledFields] = useState({
    servicePrice: true,
    kwhPrice: true,
    durationPrice: true,
    operationFee: true,
    taxes: true,
    idleTimePrice: true,
    reservePrice: true,
    moneyPerKilowattCost: true,
    customIdleTime: true,
  });

  const [exceptionType, setExceptionType] = useState(1);

  const handleDisabledFields = useCallback(() => {
    setErrors({});

    setDisabledFields((prevState) => ({
      ...prevState,
      servicePrice: !values.servicePrice,
      kwhPrice: !values.kwhPrice,
      durationPrice: !values.durationPrice,
      operationFee: !values.operationFee,
      taxes: !values.taxes,
      idleTimePrice: !values.idleTimePrice,
      reservePrice: !values.reservePrice,
      moneyPerKilowattCost: !values.moneyPerKilowattCost,
      customIdleTime: !values.customIdleTime,
    }));
  }, [setErrors, values]);

  useEffect(handleDisabledFields, [handleDisabledFields]);

  const generalPricing = useMemo(() => {
    const [generalPricing]: SetPricingPageFormValues[] =
      selectedValue?.filter(
        (item: SetPricingPageFormValues) =>
          (item.general &&
            dayOfWeek &&
            item.dayOfWeek?.toLowerCase() === dayOfWeek?.toLowerCase()) ||
          dynamicPricingDetailUuid === item.dynamicPricingDetailUuid,
      ) || [];

    return generalPricing;
  }, [selectedValue, dayOfWeek, dynamicPricingDetailUuid]);

  const initFields = useCallback(() => {
    if (generalPricing) {
      if (generalPricing.dynamicPricingDetailUuid) {
        setFieldValue(
          'dynamicPricingDetailUuid',
          generalPricing.dynamicPricingDetailUuid,
        );
      }

      if (generalPricing.servicePrice) {
        setFieldValue(
          'servicePrice',
          mascaraMoeda(Number(generalPricing.servicePrice).toFixed(2)),
        );
      }

      if (generalPricing.kwhPrice) {
        setFieldValue(
          'kwhPrice',
          mascaraMoeda(Number(generalPricing.kwhPrice).toFixed(2)),
        );
      }

      if (generalPricing.durationPrice) {
        setFieldValue(
          'durationPrice',
          mascaraMoeda(Number(generalPricing.durationPrice).toFixed(2)),
        );
      }

      if (generalPricing.operationFee) {
        setFieldValue(
          'operationFee',
          maskPercent(Number(generalPricing.operationFee) * 100),
        );
      }

      if (generalPricing.taxes) {
        setFieldValue('taxes', maskPercent(Number(generalPricing.taxes) * 100));
      }

      if (generalPricing.idleTimePrice) {
        setFieldValue(
          'idleTimePrice',
          mascaraMoeda(Number(generalPricing.idleTimePrice).toFixed(2)),
        );
      }

      if (generalPricing.reservePrice) {
        setFieldValue(
          'reservePrice',
          mascaraMoeda(Number(generalPricing.reservePrice).toFixed(2)),
        );
      }

      if (generalPricing.moneyPerKilowattCost) {
        setFieldValue(
          'moneyPerKilowattCost',
          mascaraMoeda(Number(generalPricing.moneyPerKilowattCost).toFixed(2)),
        );
      }

      if (generalPricing.startDate) {
        setFieldValue('startDate', moment(generalPricing.startDate));
      }

      if (generalPricing.endDate) {
        setExceptionType(2);
        setFieldValue('endDate', moment(generalPricing.endDate));
      }

      if (generalPricing.customIdleTime) {
        setFieldValue('customIdleTime', generalPricing.customIdleTime);
        setFieldValue('isCustomIdleTimeEnabled', true);
      }

      setDisabledFields({
        ...disabledFields,
        servicePrice: !generalPricing.servicePrice,
        kwhPrice: !generalPricing.kwhPrice,
        durationPrice: !generalPricing.durationPrice,
        operationFee: !generalPricing.operationFee,
        taxes: !generalPricing.taxes,
        idleTimePrice: !generalPricing.idleTimePrice,
        reservePrice: !generalPricing.reservePrice,
        moneyPerKilowattCost: !generalPricing.moneyPerKilowattCost,
        customIdleTime: !generalPricing.customIdleTime,
      });
    }
  }, [generalPricing, setFieldValue, disabledFields]);

  useEffect(() => {
    if (!isInitialized) {
      initFields();
      setIsInitialized(true);
    }
  }, [initFields, isInitialized]);

  useEffect(() => {
    setFieldValue('exception', isException);
  }, [setFieldValue, isException]);

  return (
    <>
      {isException && (
        <S.Row gutter={24}>
          <Col xs={24} sm={24} md={8} lg={5}>
            <S.FieldWrapper>
              <S.Label style={{ marginBottom: 8 }}>Tipo da exceção</S.Label>

              <Radio.Group
                defaultValue={1}
                disabled={loading}
                onChange={(e) => setExceptionType(e.target.value)}
                style={{ marginTop: 8 }}
                value={exceptionType}
              >
                <Radio value={1}>Dia</Radio>
                <Radio value={2}>Período</Radio>
              </Radio.Group>
            </S.FieldWrapper>
          </Col>

          <S.ColException xs={24} sm={24} md={7} lg={5}>
            <S.FieldWrapper>
              <S.Label style={{ marginBottom: 8 }}>Selecione o dia</S.Label>

              <DatePicker
                onChange={(value) => {
                  setFieldValue(
                    'startDate',
                    moment(value).format('YYYY-MM-DD'),
                  );

                  if (exceptionType === 1) {
                    setFieldValue(
                      'endDate',
                      moment(value).format('YYYY-MM-DD'),
                    );
                  }
                }}
                disabled={loading}
                format='DD/MM/YYYY'
                size='large'
                style={{ width: '100%' }}
                value={values.startDate ? moment(values.startDate) : null}
              />

              <div style={{ minHeight: 22.85 }}>
                <ErrorMessage name='startDate' component={S.MessageBox} />
              </div>
            </S.FieldWrapper>
          </S.ColException>

          {exceptionType === 2 && (
            <S.ColException xs={24} sm={24} md={7} lg={5}>
              <S.FieldWrapper>
                <S.Label style={{ marginBottom: 8 }}>Até o dia</S.Label>

                <DatePicker
                  onChange={(value) =>
                    setFieldValue('endDate', moment(value).format('YYYY-MM-DD'))
                  }
                  disabled={loading}
                  format='DD/MM/YYYY'
                  size='large'
                  style={{ width: '100%' }}
                  value={values.endDate ? moment(values.endDate) : null}
                />

                <div style={{ minHeight: 22.85 }}>
                  <ErrorMessage name='endDate' component={S.MessageBox} />
                </div>
              </S.FieldWrapper>
            </S.ColException>
          )}
        </S.Row>
      )}

      <S.Row gutter={24}>
        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  servicePrice: !enable,
                });

                if (!enable) {
                  setFieldValue('servicePrice', '');
                }
              }}
              checked={!disabledFields.servicePrice}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Valor de início</S.Label>

            <Input
              name='servicePrice'
              disabled={disabledFields.servicePrice || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.servicePrice)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue('servicePrice', mascaraMoeda(event.target.value));
              }}
              value={values.servicePrice}
            />

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper errors={!!errors.kwhPrice}>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  kwhPrice: !enable,
                  durationPrice: true,
                });

                if (!enable) {
                  setFieldValue('kwhPrice', '');
                } else {
                  setFieldValue('durationPrice', '');
                }
              }}
              checked={!disabledFields.kwhPrice}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Preço por kWh</S.Label>
            <Input
              name='kwhPrice'
              disabled={disabledFields.kwhPrice || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.kwhPrice)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue('kwhPrice', mascaraMoeda(event.target.value));
              }}
              value={values.kwhPrice}
            />

            <div style={{ minHeight: 22.85 }}>
              <ErrorMessage name='kwhPrice' component={S.MessageBox} />
            </div>
          </S.FieldWrapper>
        </S.Col>

        {/*<S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper errors={!!errors.durationPrice}>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  durationPrice: !enable,
                  kwhPrice: true,
                });

                if (!enable) {
                  setFieldValue('durationPrice', '');
                } else {
                  setFieldValue('kwhPrice', '');
                }
              }}
              checked={!disabledFields.durationPrice}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

           <S.FieldWrapper>
            <S.Label>Preço por tempo</S.Label>
            <Input
              name='durationPrice'
              disabled={disabledFields.durationPrice || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.durationPrice)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue(
                  'durationPrice',
                  mascaraMoeda(event.target.value),
                );
              }}
              value={values.durationPrice}
            />

            <div style={{ minHeight: 22.85 }}>
              <ErrorMessage name='durationPrice' component={S.MessageBox} />
            </div>
          </S.FieldWrapper>
        </S.Col>*/}

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  operationFee: !enable,
                });

                if (!enable) {
                  setFieldValue('operationFee', '');
                }
              }}
              checked={!disabledFields.operationFee}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Taxa de operação</S.Label>

            <InputMask
              name='operationFee'
              disabled={disabledFields.operationFee || loading}
              mask='99,9%'
              maskChar='0'
              size='large'
              placeholder='0,0%'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.operationFee)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={handleChange}
              value={values.operationFee}
            >
              {(inputProps) => (
                <Input
                  {...inputProps}
                  disabled={disabledFields.operationFee || loading}
                />
              )}
            </InputMask>

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  taxes: !enable,
                });

                if (!enable) {
                  setFieldValue('taxes', '');
                }
              }}
              checked={!disabledFields.taxes}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Impostos</S.Label>
            <InputMask
              name='taxes'
              disabled={disabledFields.taxes || loading}
              mask='99,9%'
              maskChar='0'
              size='large'
              placeholder='0,0%'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.taxes)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={handleChange}
              value={values.taxes}
            >
              {(inputProps) => (
                <Input
                  {...inputProps}
                  disabled={disabledFields.taxes || loading}
                />
              )}
            </InputMask>

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  idleTimePrice: !enable,
                });

                if (!enable) {
                  setFieldValue('idleTimePrice', '');
                }
              }}
              checked={!disabledFields.idleTimePrice}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Preço tempo ociosidade conector</S.Label>
            <Input
              name='idleTimePrice'
              disabled={disabledFields.idleTimePrice || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.idleTimePrice)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue(
                  'idleTimePrice',
                  mascaraMoeda(event.target.value),
                );
              }}
              value={values.idleTimePrice}
            />

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  reservePrice: !enable,
                });

                if (!enable) {
                  setFieldValue('reservePrice', '');
                }
              }}
              checked={!disabledFields.reservePrice}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Valor da reserva</S.Label>
            <Input
              name='reservePrice'
              disabled={disabledFields.reservePrice || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.reservePrice)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue('reservePrice', mascaraMoeda(event.target.value));
              }}
              value={values.reservePrice}
            />

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  moneyPerKilowattCost: !enable,
                });

                if (!enable) {
                  setFieldValue('moneyPerKilowattCost', '');
                }
              }}
              checked={!disabledFields.moneyPerKilowattCost}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Custo por kWh</S.Label>
            <Input
              name='moneyPerKilowattCost'
              disabled={disabledFields.moneyPerKilowattCost || loading}
              size='large'
              placeholder='R$ 0,00'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.moneyPerKilowattCost)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue(
                  'moneyPerKilowattCost',
                  mascaraMoeda(event.target.value),
                );
              }}
              value={values.moneyPerKilowattCost}
            />

            <div style={{ minHeight: 22.85 }} />
          </S.FieldWrapper>
        </S.Col>

        <S.Col xs={24} sm={24} md={12} lg={8}>
          <S.SwitchWrapper>
            <Switch
              disabled={loading}
              onChange={(enable) => {
                setDisabledFields({
                  ...disabledFields,
                  customIdleTime: !enable,
                });

                setFieldValue('isCustomIdleTimeEnabled', enable);

                if (enable && !values.customIdleTime) {
                  setFieldValue('customIdleTime', 20);
                }

                if (!enable) {
                  setFieldValue('customIdleTime', 0);
                }
              }}
              checked={!disabledFields.customIdleTime}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </S.SwitchWrapper>

          <S.FieldWrapper>
            <S.Label>Tempo ociosidade (minutos)</S.Label>
            <Input
              name='customIdleTime'
              disabled={disabledFields.customIdleTime || loading}
              size='large'
              placeholder='20'
              suffix={
                <Tooltip title={T(tooltipDynamicPricing.customIdleTime)}>
                  <InfoCircleOutlined />
                </Tooltip>
              }
              onChange={(event) => {
                setFieldValue(
                  'customIdleTime',
                  rawDigitsMask(event.target.value),
                );
              }}
              value={values.customIdleTime}
            />

            <div style={{ minHeight: 22.85 }}>
              <ErrorMessage name='customIdleTime' component={S.MessageBox} />
            </div>
          </S.FieldWrapper>
        </S.Col>
      </S.Row>
    </>
  );
};

export default SetPricingPageForm;
